import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import type {AppDispatch, RootState} from '@/store'
import {setTheme, setFavorites} from '@/store/slices/theme'
import {loadAllTableSettings} from '@/store/slices/tableSettingsSlice'
import {fetchUserInfo, getCurrentUserEmail} from '@/utils/userHelper'
import {parseTableSettingsFromUserResponse} from '@/services/tableSettingsService'

/**
 * Hook to fetch and set user preferences (theme and favorites) from API on app load
 * Also stores the user ID in localStorage for favorites API calls
 * Only runs once when the hook is mounted
 */
export const useUserTheme = () => {
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    const loadUserPreferences = async () => {
      try {
        const userEmail = getCurrentUserEmail()
        if (userEmail) {
          const userInfo = await fetchUserInfo(userEmail)

          if (userInfo) {
            // Store user ID in localStorage for favorites API calls
            if (userInfo.id) {
              localStorage.setItem('userId', userInfo.id)
            }

            // Set theme
            if (userInfo.theme) {
              dispatch(setTheme(userInfo.theme))
            }

            // Set favorites - with null check for main branch compatibility
            if (userInfo.favourites && Array.isArray(userInfo.favourites) && userInfo.favourites.length > 0) {
              dispatch(setFavorites(userInfo.favourites))
            } else {
              // If favourites is undefined or null, set empty array
              dispatch(setFavorites([]))
            }

            // Hydrate table settings from backend
            if (userInfo.seMetricsTableSettings) {
              const settings = parseTableSettingsFromUserResponse(userInfo.seMetricsTableSettings)
              if (Object.keys(settings).length > 0) {
                dispatch(loadAllTableSettings(settings))
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to load user preferences:', error)
      }
    }

    loadUserPreferences()
  }, [dispatch])
}

/**
 * Hook to check and manage favorite status for a dashboard item
 * @param itemId - The ID of the item (team or program)
 * @param itemType - The type of the item ('Team Dashboard' or 'Program Dashboard')
 * @returns Object containing isFavorite state and setter
 *
 * @example
 * const {isFavorite, setIsFavorite} = useFavoriteStatus(teamId, 'Team Dashboard')
 */
export const useFavoriteStatus = (itemId: string | undefined, itemType: 'Team Dashboard' | 'Program Dashboard') => {
  const userFavorites = useSelector((state: RootState) => state.theme.favorites)

  // Check if this item is already in favorites - with null check for main branch compatibility
  const isInFavorites =
    itemId && userFavorites && Array.isArray(userFavorites)
      ? userFavorites.some(fav => fav.id === itemId && fav.type === itemType)
      : false

  // Initialize state based on whether it's already in favorites
  const [isFavorite, setIsFavorite] = useState(isInFavorites)

  // Update favorite state if userFavorites changes
  useEffect(() => {
    if (itemId && userFavorites && Array.isArray(userFavorites)) {
      const isNowInFavorites = userFavorites.some(fav => fav.id === itemId && fav.type === itemType)
      setIsFavorite(isNowInFavorites)
    }
  }, [userFavorites, itemId, itemType])

  return {isFavorite, setIsFavorite}
}
